import React, { useState, useEffect, useRef  } from "react";
import { useNavigate } from "react-router-dom";
import "./NotificationMenu.css";
import {
  getNotificaciones,
  marcarNotificacionClick,
  marcarNotificacionesLeidas,
  getEstadoOnboarding,
} from "../../services/notificaciones";
import OnboardingCard from "./OnboardingCard"; // Ajusta la ruta si la pusiste en otro lado
import { getCitas } from "../../services/citas";

const NotificationMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [estadoOnboarding, setEstadoOnboarding] = useState(null);
  const [mostrarAtencion, setMostrarAtencion] = useState(false);
  const navigate = useNavigate();
  const menuRef = useRef(null);

  const cargarCitasDelDia = async () => {
    try {
      const citas = await getCitas();
      const ahora = new Date();
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);

      const citasHoyFiltradas = citas.filter((cita) => {
        if (!cita.start_time) return false;
        const citaFecha = new Date(`${cita.fecha}T00:00:00`);
        if (citaFecha.getTime() !== hoy.getTime()) return false;
        const fechaHoraCita = new Date(`${cita.fecha}T${cita.start_time}`);
        const diferenciaMinutos = (fechaHoraCita - ahora) / 1000 / 60;
        return diferenciaMinutos >= -120 && diferenciaMinutos <= 120;
      });

      const formatTime = (timeStr) => {
        if (!timeStr) return "";
        const [hh, mm] = timeStr.split(":");
        return `${hh}:${mm}`;
      };

      return citasHoyFiltradas.map((cita) => {
        const esPersonal = !cita.paciente && !cita.paciente_nombre;
        const pacienteTexto = esPersonal
          ? "Cita personal"
          : `Cita con ${cita.paciente_nombre}`;
        return {
          id: `cita-${cita.id}`,           // prefijo para distinguir de notifs reales
          mensaje: `${pacienteTexto} de ${formatTime(cita.start_time)} a ${formatTime(cita.end_time)}`,
          efimera: true,                   // bandera para saber que NO es de DB
          cta_url: "/dashboard/citas",
        };
      });
    } catch (error) {
      console.error("❌ Error al cargar citas para notificaciones:", error);
      return [];
    }
  };

 const cargarTodo = async () => {
  try {
    const [notifs, onboarding, citasEfimeras] = await Promise.all([
      getNotificaciones(),
      getEstadoOnboarding(),
      cargarCitasDelDia(),
    ]);
    const lista = Array.isArray(notifs) ? notifs : notifs.results || [];
    // Las efímeras van arriba (son más urgentes en el momento)
    setNotifications([...citasEfimeras, ...lista]);
    setEstadoOnboarding(onboarding);
  } catch (error) {
    console.error("❌ Error al cargar notificaciones/onboarding:", error);
  }
};

  useEffect(() => {
  cargarTodo();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  useEffect(() => {
  const handleClickFuera = (event) => {
    if (menuRef.current && !menuRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  if (isOpen) {
    document.addEventListener("mousedown", handleClickFuera);
  }

  return () => {
    document.removeEventListener("mousedown", handleClickFuera);
  };
}, [isOpen]);

  // Badge: no leídas + 1 si el onboarding está activo (cuenta como una sola cosa pendiente)
const unreadNotifs = notifications.filter((n) => !n.leida).length;
const onboardingPendiente = estadoOnboarding?.onboarding_activo ? 1 : 0;
const unreadCount = unreadNotifs + onboardingPendiente;

useEffect(() => {
  if (unreadCount <= 0 || isOpen) return;

  const yaSeMostro = sessionStorage.getItem("salvir_notif_attention_seen");
  if (yaSeMostro) return;

  sessionStorage.setItem("salvir_notif_attention_seen", "true");

  const startTimer = setTimeout(() => {
    setMostrarAtencion(true);
  }, 1600);

  const stopTimer = setTimeout(() => {
    setMostrarAtencion(false);
  }, 10300);

  return () => {
    clearTimeout(startTimer);
    clearTimeout(stopTimer);
  };
}, [unreadCount, isOpen]);

  const handleClick = async (notif) => {
  // Si es efímera (ej. cita del día), solo navega. No se marca en backend.
  if (notif.efimera) {
    if (notif.cta_url) {
      setIsOpen(false);
      navigate(notif.cta_url);
    }
    return;
  }

  // Notificaciones reales: marcar click en backend
  if (!notif.click) {
    setNotifications((prev) =>
      prev.map((n) =>
        n.id === notif.id ? { ...n, leida: true, click: true } : n
      )
    );
    marcarNotificacionClick(notif.id).catch((err) =>
      console.error("No se pudo marcar click:", err)
    );
  }
  if (notif.cta_url) {
    setIsOpen(false);
    
    // Pantallas del dashboard: misma pestaña (como siempre).
    // Todo lo demás (términos, aviso, PDFs, páginas externas): pestaña nueva.
    if (notif.cta_url.startsWith("/dashboard")) {
      navigate(notif.cta_url);
    } else {
      window.open(notif.cta_url, "_blank", "noopener,noreferrer");
    }
  }
};

  const handleOnboardingClick = (cta_url) => {
  setIsOpen(false);
  navigate(cta_url);
  // Después de un momento, recargar el estado del onboarding
  setTimeout(() => {
    cargarTodo();
  }, 500);
};

  const mostrarOnboarding = estadoOnboarding?.onboarding_activo;
  const hayNotificaciones = notifications.length > 0;
  

  return (
    <div className="notification-menu" ref={menuRef}>
      <div
          className={`notification-icon ${mostrarAtencion ? "notification-attention" : ""}`}
          onClick={async () => {
            const abrir = !isOpen;
            setIsOpen(abrir);
            setMostrarAtencion(false);
            
            if (abrir) {
              await cargarTodo();
              // Si hay no-leídas, marcarlas y actualizar UI
              setNotifications((prev) => {
                const hayNoLeidas = prev.some((n) => !n.leida);
                if (hayNoLeidas) {
                  marcarNotificacionesLeidas().catch(() => {});
                  return prev.map((n) => ({ ...n, leida: true }));
                }
                return prev;
              });
            }
          }}
        >
        <span role="img" aria-label="notificaciones">
          🔔
        </span>
        {unreadCount > 0 && (
          <span className="notification-count">{unreadCount}</span>
        )}
      </div>

      {isOpen && (
        <div className="notification-dropdown">
          {mostrarOnboarding && (
            <OnboardingCard
              estado={estadoOnboarding}
              onNavigate={handleOnboardingClick}
            />
          )}

          {hayNotificaciones ? (
            <ul>
              {notifications.map((notif) => (
                <li
                  key={notif.id}
                  className={`notification-item ${
                    notif.leida ? "leida" : "no-leida"
                  } ${notif.cta_url ? "clickeable" : ""}`}
                  onClick={() => handleClick(notif)}
                >
                  <div className="notification-mensaje">{notif.mensaje}</div>
                  {notif.cta_texto && (
                    <div className="notification-cta">
                      {notif.cta_texto} →
                    </div>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            !mostrarOnboarding && (
              <div className="notification-vacio">
                No tienes notificaciones
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationMenu;